<script setup lang="ts">
const RIGHT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/f4122ad0-2700-484d-85e5-7fc2ce51220a.png'
const LEFT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/b20f9767-20ee-4d76-b98f-927eb724c8f6.png'
const CLOSE_IMG = 'https://resources1.interface003.com/web30/2026/06/24/79597aa8-cc8a-4b81-a642-c0579965981e.png'
const VIDEO_CLOSE_IMG = 'https://resources1.interface003.com/web30/2026/08/25/bc2282f3-8109-4d35-bfbd-34c3bf56b927.png'
type ViewImageItem =
  | string
  | {
      detail?: string
      list?: string
      url?: string
    }

type ViewImageGroupItem = {
  images?: ViewImageItem[]
}

/** 公共查看详情弹层，兼容旧版图片/视频预览入参与当前项目的 v-model 控制方式。 */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    imgList?: ViewImageItem[]
    imgLists?: ViewImageItem[] | ViewImageGroupItem[]
    address?: string
    picUrlind?: string | number
    picDataind?: string | number
  }>(),
  {
    modelValue: false,
    imgList: () => [],
    imgLists: () => [],
    address: '',
    picUrlind: 0,
    picDataind: 0,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  closevideo: []
}>()

/** 统一对外暴露弹层开关状态，兼容父层 v-model。 */
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/** 当前是否为 mp4 视频预览。 */
const isMp4 = computed(() => `${props.address || ''}`.toLowerCase().includes('.mp4'))

/** 将任意图片项统一提取成可渲染的图片地址。 */
const resolveImageSrc = (item: ViewImageItem) => {
  if (typeof item === 'string') {
    return item
  }

  return item?.detail || item?.list || item?.url || ''
}

/** 将任意索引归一化到列表范围内。 */
const normalizedIndex = (index: number, total: number) => {
  if (total <= 0) {
    return 0
  }

  return ((index % total) + total) % total
}

/** 兼容旧版 imgLists[picDataind].images 与直接传数组两种结构。 */
const previewImageList = computed(() => {
  if (Array.isArray(props.imgList) && props.imgList.length > 0) {
    return props.imgList.map(resolveImageSrc).filter(Boolean)
  }

  if (!Array.isArray(props.imgLists) || props.imgLists.length === 0) {
    return []
  }

  const groupIndex = Number(props.picDataind) || 0
  const currentGroup = props.imgLists[groupIndex] as ViewImageGroupItem | ViewImageItem | undefined

  if (
    currentGroup &&
    typeof currentGroup === 'object' &&
    'images' in currentGroup &&
    Array.isArray(currentGroup.images)
  ) {
    return currentGroup.images.map(resolveImageSrc).filter(Boolean)
  }

  return (props.imgLists as ViewImageItem[]).map(resolveImageSrc).filter(Boolean)
})

/** 当前正在预览的图片索引。 */
const currentIndex = ref(0)
/** 图片切换方向，用于控制进入离场动画。 */
const transitionDirection = ref<'prev' | 'next'>('next')

watch(
  () => [props.picUrlind, previewImageList.value.length, visible.value],
  ([index, total]) => {
    currentIndex.value = normalizedIndex(Number(index) || 0, Number(total) || 0)
  },
  {
    immediate: true,
  },
)

/** 当前大图地址。 */
const currentImage = computed(() => previewImageList.value[currentIndex.value] || '')
/** 是否存在多张图片。 */
const hasMultipleImages = computed(() => previewImageList.value.length > 1)

/** 按方向切换当前图片。 */
const switchImage = (step: number) => {
  if (!hasMultipleImages.value) {
    return
  }

  transitionDirection.value = step < 0 ? 'prev' : 'next'
  currentIndex.value = normalizedIndex(currentIndex.value + step, previewImageList.value.length)
}

const prevImage = () => switchImage(-1)
const nextImage = () => switchImage(1)

/** 关闭预览弹层，同时兼容旧版 closevideo 事件。 */
const closeView = () => {
  visible.value = false
  emit('closevideo')
}

/** 兼容旧版实例方法调用。 */
const open = () => {
  visible.value = true
}

defineExpose({
  open,
  closeView,
})
</script>

<template>
  <ElDialog
    v-model="visible"
    width="760px"
    align-center
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    class="common-view-details-dialog"
  >
    <div v-if="isMp4 && address" class="relative overflow-hidden rounded-[28px] bg-white">
      <div class="relative overflow-hidden rounded-[28px]">
        <video
          id="video"
          :src="address"
          type="video/mp4"
          class="block h-[428px] w-full bg-black object-cover"
          autoplay
          controls="controls"
          loop="-1"
          preload
          muted
          controlslist="nodownload noplaybackrate"
          disablePictureInPicture
          noplaybackrate
        ></video>

        <button
          type="button"
          class="common-view-details-dialog__video-close absolute top-[12px] right-[12px] z-[3] flex size-[22px] cursor-pointer items-center justify-center bg-transparent p-0 rtl:right-auto rtl:left-[12px]"
          aria-label="Close video preview"
          @click="closeView"
        >
          <CommonImage class="size-[22px]" :src="VIDEO_CLOSE_IMG" alt="" />
        </button>
      </div>
    </div>

    <div v-else class="relative overflow-hidden rounded-[28px] bg-white">
      <div class="relative h-[428px] overflow-hidden rounded-[12px]">
        <Transition :name="transitionDirection === 'prev' ? 'view-details-prev' : 'view-details-next'">
          <CommonImage
            v-if="currentImage"
            :key="currentImage"
            :src="currentImage"
            alt="preview image"
            class="absolute inset-0 block size-full"
            object-fit="contain"
          />
          <div
            v-else
            key="empty"
            class="absolute inset-0 flex size-full items-center justify-center bg-[#f5f6fa] text-[14px] leading-[22px] text-[#7d818a]"
          >
            --
          </div>
        </Transition>

        <button
          type="button"
          class="absolute top-[32px] right-[32px] z-[3] flex size-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)] rtl:right-auto rtl:left-[32px]"
          @click="closeView"
        >
          <CommonImage class="size-[12px]" :src="CLOSE_IMG" alt="close" />
        </button>

        <template v-if="hasMultipleImages">
          <button
            type="button"
            class="absolute top-1/2 left-[20px] z-[2] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)] rtl:right-[20px] rtl:left-auto rtl:rotate-180"
            @click="prevImage"
          >
            <CommonImage :src="LEFT_IMG" alt="prev" class="size-[20px]" />
          </button>

          <button
            type="button"
            class="absolute top-1/2 right-[20px] z-[2] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)] rtl:right-auto rtl:left-[20px] rtl:rotate-180"
            @click="nextImage"
          >
            <CommonImage :src="RIGHT_IMG" alt="next" class="size-[20px]" />
          </button>

          <div
            class="absolute right-[20px] bottom-[20px] z-[2] min-w-[54px] rounded-[999px] bg-[rgba(5,12,31,0.46)] px-[16px] py-[6px] text-[14px] leading-[20px] text-white rtl:right-auto rtl:left-[20px]"
          >
            {{ currentIndex + 1 }}/{{ previewImageList.length }}
          </div>
        </template>
      </div>
    </div>
  </ElDialog>
</template>

<style scoped lang="less">
:global(.common-view-details-dialog) {
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow:
    0 8px 12px 0 rgba(30, 31, 33, 0.15),
    0 0 1px 0 rgba(30, 31, 33, 0.31);
  padding: 0;
}

:global(.common-view-details-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.common-view-details-dialog .el-dialog__body) {
  padding: 0;
}

:global(.common-view-details-dialog .el-dialog) {
  margin: 0;
}

:global(.view-details-next-enter-active),
:global(.view-details-next-leave-active),
:global(.view-details-prev-enter-active),
:global(.view-details-prev-leave-active) {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

:global(.view-details-next-enter-active),
:global(.view-details-next-leave-active),
:global(.view-details-prev-enter-active),
:global(.view-details-prev-leave-active),
:global(.view-details-next-enter-from),
:global(.view-details-next-leave-to),
:global(.view-details-prev-enter-from),
:global(.view-details-prev-leave-to) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

:global(.view-details-next-enter-from) {
  opacity: 0;
  transform: translateX(18px) scale(0.99);
}

:global(.view-details-next-leave-to) {
  opacity: 0;
  transform: translateX(-18px) scale(0.99);
}

:global(.view-details-prev-enter-from) {
  opacity: 0;
  transform: translateX(-18px) scale(0.99);
}

:global(.view-details-prev-leave-to) {
  opacity: 0;
  transform: translateX(18px) scale(0.99);
}
</style>
